import { create } from 'zustand'
import { supabase } from '@/lib/supabase'
import type { FamilyMember, FamilyMemberFormData } from '@/types'

interface FamilyState {
  members: FamilyMember[]
  loading: boolean
  error: string | null
  fetchMembers: () => Promise<void>
  addMember: (data: FamilyMemberFormData) => Promise<FamilyMember | null>
  updateMember: (id: string, data: Partial<FamilyMemberFormData>) => Promise<void>
  deleteMember: (id: string) => Promise<void>
  uploadAvatar: (file: File) => Promise<string | null>
}

export const useFamilyStore = create<FamilyState>((set) => ({
  members: [],
  loading: false,
  error: null,

  fetchMembers: async () => {
    set({ loading: true, error: null })
    try {
      const { data, error } = await supabase
        .from('family_members')
        .select('*')
        .order('created_at', { ascending: true })

      if (error) throw error
      set({ members: data || [], loading: false })
    } catch (error) {
      set({ error: (error as Error).message, loading: false })
    }
  },

  addMember: async (data: FamilyMemberFormData) => {
    set({ loading: true, error: null })
    try {
      const { data: newMember, error } = await supabase
        .from('family_members')
        // @ts-ignore - Supabase type inference issue with Insert types
        .insert([data])
        .select()
        .single()

      if (error) throw error

      set((state) => ({
        members: [...state.members, newMember],
        loading: false,
      }))

      return newMember
    } catch (error) {
      set({ error: (error as Error).message, loading: false })
      return null
    }
  },

  updateMember: async (id: string, data: Partial<FamilyMemberFormData>) => {
    set({ loading: true, error: null })
    try {
      const { error } = await supabase
        .from('family_members')
        // @ts-ignore - Supabase type inference issue with Update types
        .update(data)
        .eq('id', id)

      if (error) throw error

      set((state) => ({
        members: state.members.map((m) =>
          m.id === id ? { ...m, ...data, updated_at: new Date().toISOString() } : m
        ),
        loading: false,
      }))
    } catch (error) {
      set({ error: (error as Error).message, loading: false })
    }
  },

  deleteMember: async (id: string) => {
    set({ loading: true, error: null })
    try {
      const { error } = await supabase
        .from('family_members')
        .delete()
        .eq('id', id)

      if (error) throw error

      set((state) => ({
        members: state.members.filter((m) => m.id !== id),
        loading: false,
      }))
    } catch (error) {
      set({ error: (error as Error).message, loading: false })
    }
  },

  uploadAvatar: async (file: File) => {
    try {
      const fileExt = file.name.split('.').pop()
      const fileName = `${Math.random()}.${fileExt}`
      const filePath = `avatars/${fileName}`

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file)

      if (uploadError) throw uploadError

      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath)

      return data.publicUrl
    } catch (error) {
      console.error('Avatar upload error:', error)
      return null
    }
  },
}))
